import { useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { enqueueSnackbar } from 'notistack';
import {
  useFetchOAuthStatusQuery,
  useLazyGetAuthorizeUrlQuery,
  useRefreshPlatformTokenMutation,
  useDisconnectPlatformMutation,
} from '../../slices/oauthSlice';

interface PlatformStatus {
  platform: string;
  status: string;
  last_refreshed_at: string | null;
  access_token_expiry: string | null;
  refresh_token_expiry: string | null;
  error_message: string | null;
  updated_at: string | null;
}

interface PlatformConnectionCardProps {
  platformStatus: PlatformStatus;
  displayName: string;
}

const statusConfig: Record<
  string,
  { color: 'success' | 'default' | 'error' | 'warning'; label: string }
> = {
  active: { color: 'success', label: 'Connected' },
  disconnected: { color: 'default', label: 'Disconnected' },
  error: { color: 'error', label: 'Error' },
  expired: { color: 'warning', label: 'Expired' },
  revoked: { color: 'error', label: 'Revoked' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString();
}

function getTimeUntilExpiry(dateStr: string | null): string {
  if (!dateStr) return '';
  const expiry = new Date(dateStr);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  if (diffMs <= 0) return 'Expired';
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (diffHrs > 24) {
    const days = Math.floor(diffHrs / 24);
    return `${days}d ${diffHrs % 24}h`;
  }
  return `${diffHrs}h ${diffMins}m`;
}

const PlatformConnectionCard: React.FC<PlatformConnectionCardProps> = ({
  platformStatus,
  displayName,
}) => {
  const [getAuthorizeUrl, { isLoading: isLoadingAuth }] =
    useLazyGetAuthorizeUrlQuery();
  const [refreshToken, { isLoading: isRefreshing }] =
    useRefreshPlatformTokenMutation();
  const [disconnectPlatform, { isLoading: isDisconnecting }] =
    useDisconnectPlatformMutation();
  const { refetch } = useFetchOAuthStatusQuery();

  const {
    platform,
    status,
    last_refreshed_at,
    access_token_expiry,
    error_message,
  } = platformStatus;
  const config = statusConfig[status] || statusConfig.disconnected;
  const isConnected = status === 'active';

  const handleOAuthMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type !== 'oauth_callback') return;
      if (event.data.platform !== platform) return;

      if (event.data.status === 'success') {
        enqueueSnackbar(`${displayName} connected successfully`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          `${displayName} connection failed: ${event.data.message || 'Unknown error'}`,
          { variant: 'error' }
        );
      }
      refetch();
    },
    [platform, displayName, refetch]
  );

  useEffect(() => {
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [handleOAuthMessage]);

  const handleConnect = async () => {
    try {
      const result = await getAuthorizeUrl(platform).unwrap();
      window.open(
        result.authorize_url,
        '_blank',
        'width=500,height=650,popup=yes'
      );
    } catch {
      enqueueSnackbar('Failed to get authorization URL', { variant: 'error' });
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshToken(platform).unwrap();
      enqueueSnackbar(`${displayName} token refreshed`, { variant: 'success' });
    } catch {
      enqueueSnackbar(`Failed to refresh ${displayName} token`, {
        variant: 'error',
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectPlatform(platform).unwrap();
      enqueueSnackbar(`${displayName} disconnected`, { variant: 'info' });
    } catch {
      enqueueSnackbar(`Failed to disconnect ${displayName}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Card variant="outlined" sx={{ minWidth: 300, flex: 1 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">{displayName}</Typography>
          <Chip label={config.label} color={config.color} size="small" />
        </Stack>

        {isConnected && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Last refreshed: {formatDate(last_refreshed_at)}
            </Typography>
            {access_token_expiry && (
              <Typography variant="body2" color="text.secondary">
                Token expires in: {getTimeUntilExpiry(access_token_expiry)}
              </Typography>
            )}
          </Box>
        )}

        {error_message && (
          <Typography variant="body2" color="error" mb={2}>
            {error_message}
          </Typography>
        )}

        <Stack direction="row" spacing={1}>
          {!isConnected ? (
            <Button
              variant="contained"
              startIcon={
                isLoadingAuth ? <CircularProgress size={16} /> : <LinkIcon />
              }
              onClick={handleConnect}
              disabled={isLoadingAuth}
              size="small"
            >
              Connect
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={
                  isRefreshing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={handleRefresh}
                disabled={isRefreshing}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={
                  isLoadingAuth ? <CircularProgress size={16} /> : <LinkIcon />
                }
                onClick={handleConnect}
                disabled={isLoadingAuth}
                size="small"
              >
                Reconnect
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={
                  isDisconnecting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <LinkOffIcon />
                  )
                }
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                size="small"
              >
                Disconnect
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PlatformConnectionCard;
